import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Table, Alert, Modal, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import { api } from '../../../shared/api';
import Select from '../../../components/form/AppSelect';
import '../../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../shared/permissions';
import { useDateFormat } from '../../../shared/DateFormatContext';
import { useConfirm } from '../../../shared/ConfirmContext';
import { useInputHistory } from '../../../shared/useInputHistory';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import EmptyState from '../../../components/feedback/EmptyState';
import PageSection from '../../../components/layout/PageSection';
import StatusBadge from '../../../components/ui/StatusBadge';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

const ProjectTasks = ({ projectId, projectStartDate, projectEndDate }) => {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const [taskStatuses, setTaskStatuses] = useState([]);
  const [taskPriorities, setTaskPriorities] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [crewOptions, setCrewOptions] = useState([]);

  const { isLoading: loading, data: queryData } = useQuery({
    queryKey: ['project-tasks', projectId],
    queryFn: async () => {
      const [tasksRes, statusesRes, prioritiesRes, crewRes] = await Promise.all([
        api.get(`projects/api/project-tasks/?project_id=${projectId}`),
        api.get('projects/api/project-task-statuses/'),
        api.get('projects/api/project-task-priorities/'),
        api.get('crew/api/crew/'),
      ]);
      setTasks(tasksRes);
      setAllTasks(tasksRes);
      if (Array.isArray(tasksRes)) {
        tasksRes.forEach(task => { if (task.title) titleHistory.add(task.title); });
      }
      setTaskStatuses(statusesRes);
      setTaskPriorities(prioritiesRes);
      setCrewMembers(crewRes);
      return { tasks: tasksRes, statuses: statusesRes, priorities: prioritiesRes, crew: crewRes };
    },
  });

  useEffect(() => {
    if (!queryData) return;
    const fetchedTasks = queryData.tasks || [];
    setTasks(fetchedTasks);
    setAllTasks(fetchedTasks);
    fetchedTasks.forEach(task => { if (task.title) titleHistory.add(task.title); });
    setTaskStatuses(queryData.statuses || []);
    setTaskPriorities(queryData.priorities || []);
    setCrewMembers(queryData.crew || []);
  }, [queryData, titleHistory]);

  const fetchTasks = useCallback((silent = false) => {
    queryClient.invalidateQueries({ queryKey: ['project-tasks', projectId] });
  }, [queryClient, projectId]);

  const [formData, setFormData] = useState({
    project: projectId,
    title: '',
    description: '',
    status: '',
    priority: '',
    due_date: '',
    assigned_to: []
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  const titleHistory = useInputHistory('project_task_title_history');
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [showTitleSugg, setShowTitleSugg] = useState(false);
  const titleInputRef = useRef(null);
  const _tasksDebounce = useRef(null);

  useRealtimeEvents(useCallback((e) => {
    if (e.model !== 'projects.projecttask') return;
    clearTimeout(_tasksDebounce.current);
    _tasksDebounce.current = setTimeout(() => fetchTasks(true), 400);
  }, [fetchTasks])); // eslint-disable-line react-hooks/exhaustive-deps

  // crewOptions effect kept, fetchTasks/fetchTaskStatuses/fetchTaskPriorities/fetchCrewMembers removed
  useEffect(() => {
    setCrewOptions([
      { value: null, label: t('projects.tasks.unassigned') },
      ...crewMembers.map(m => ({ value: m.id, label: m.display_name || `${m.first_name} ${m.last_name}` }))
    ]);
  }, [crewMembers, t]);

  // Client-side filtering
  useEffect(() => {
    let filtered = [...allTasks];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        (task.title || '').toLowerCase().includes(q) ||
        (task.description && task.description.toLowerCase().includes(q))
      );
    }
    if (selectedStatus && selectedStatus.value !== null) {
      filtered = filtered.filter(task => task.status === selectedStatus.value);
    }
    if (selectedPriority && selectedPriority.value !== null) {
      filtered = filtered.filter(task => task.priority === selectedPriority.value);
    }
    if (selectedAssignee) {
      if (selectedAssignee.value === null) {
        filtered = filtered.filter(task => !task.assigned_to || task.assigned_to.length === 0);
      } else {
        filtered = filtered.filter(task => task.assigned_to && task.assigned_to.includes(selectedAssignee.value));
      }
    }
    setTasks(filtered);
  }, [searchQuery, selectedStatus, selectedPriority, selectedAssignee, allTasks]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'title') {
      setTitleSuggestions(titleHistory.getSuggestions(value));
      setShowTitleSugg(true);
    }
  };

  const handleTitleFocus = () => {
    setTitleSuggestions(titleHistory.getSuggestions(formData.title));
    setShowTitleSugg(true);
  };

  const handleTitleBlur = () => {
    // Delay so onMouseDown on a suggestion fires first
    setTimeout(() => setShowTitleSugg(false), 150);
  };

  const handlePickSuggestion = (value) => {
    setFormData(f => ({ ...f, title: value }));
    setShowTitleSugg(false);
    titleInputRef.current?.focus();
  };

  const handleAssignedToChange = (selectedOption) => {
    setFormData({
      ...formData,
      assigned_to: selectedOption && selectedOption.value !== null ? [selectedOption.value] : []
    });
  };

  const resetForm = () => {
    setFormData({
      project: projectId,
      title: '',
      description: '',
      status: taskStatuses[0]?.id || '',
      priority: taskPriorities[0]?.id || '',
      due_date: '',
      assigned_to: []
    });
    setEditingTaskId(null);
  };

  const handleOpenCreate = () => {
    setError(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (task) => {
    setFormData({
      project: task.project,
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      due_date: task.due_date || '',
      assigned_to: task.assigned_to || []
    });
    setEditingTaskId(task.id);
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTaskId) {
        await api.patch(`projects/api/project-tasks/${editingTaskId}/`, formData);
      } else {
        await api.post('projects/api/project-tasks/', formData);
      }
      titleHistory.add(formData.title);
      setShowModal(false);
      resetForm();
      setError(null);
      fetchTasks();
    } catch (err) {
      console.error('Error saving task:', err);
      setError(t(editingTaskId ? 'projects.tasks.errorUpdating' : 'projects.tasks.errorCreating'));
    }
  };

  const handleDelete = async (taskId) => {
    if (await confirm(t('projects.tasks.confirmDelete'))) {
      try {
        await api.delete(`projects/api/project-tasks/${taskId}/`);
        setError(null);
        fetchTasks();
      } catch (err) {
        console.error('Error deleting task:', err);
        setError(t('projects.tasks.errorDeleting'));
      }
    }
  };

  const getStatusBadge = (task) => {
    const detail = task.status_detail;
    return <StatusBadge color={detail?.color || '#6c7a91'} label={detail?.name || t('common.notSet')} />;
  };

  const getPriorityBadge = (task) => {
    const detail = task.priority_detail;
    return <StatusBadge color={detail?.color || '#6c7a91'} label={detail?.name || t('common.notSet')} />;
  };

  const getAssignedToDisplay = (task) => {
    const details = task.assigned_to_details;
    if (!details || details.length === 0) return t('projects.tasks.unassigned');
    return details.map(m => m.display_name).join(', ');
  };

  const renderTimeline = () => {
    const start = projectStartDate ? new Date(projectStartDate) : null;
    const end = projectEndDate ? new Date(projectEndDate) : null;
    const totalMs = start && end ? end - start : 0;

    const tasksWithDate = tasks.filter(task => task.due_date);
    const tasksWithoutDate = tasks.filter(task => !task.due_date);

    if (!start || !end || totalMs <= 0) {
      return (
        <div className="text-muted p-3">
          {t('projects.tasks.noTimelineRange')}
        </div>
      );
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        {/* Month labels */}
        <div style={{ position: 'relative', height: '24px', marginBottom: '4px', minWidth: '600px' }}>
          {(() => {
            const labels = [];
            const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
            while (cursor <= end) {
              const pct = Math.max(0, (cursor - start) / totalMs * 100);
              labels.push(
                <span key={cursor.toISOString()} style={{
                  position: 'absolute',
                  left: `${pct}%`,
                  fontSize: '11px',
                  color: 'var(--tblr-body-color-secondary)',
                  whiteSpace: 'nowrap'
                }}>
                  {cursor.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
                </span>
              );
              cursor.setMonth(cursor.getMonth() + 1);
            }
            return labels;
          })()}
        </div>

        {/* Timeline track */}
        <div style={{
          position: 'relative',
          height: '8px',
          background: 'var(--tblr-border-color)',
          borderRadius: '4px',
          marginBottom: '16px',
          minWidth: '600px'
        }}>
          <div style={{
            position: 'absolute',
            left: 0, right: 0, top: 0, bottom: 0,
            background: 'var(--tblr-primary)',
            opacity: 0.15,
            borderRadius: '4px'
          }} />
        </div>

        {/* Task rows */}
        <div style={{ minWidth: '600px' }}>
          {tasksWithDate.map(task => {
            const taskDate = new Date(task.due_date);
            const pct = Math.min(100, Math.max(0, (taskDate - start) / totalMs * 100));
            const color = task.priority_detail?.color || '#6c757d';
            return (
              <div key={task.id} style={{ position: 'relative', height: '32px', marginBottom: '6px' }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'var(--tblr-border-color)',
                  transform: 'translateY(-50%)'
                }} />
                <div
                  title={`${task.title} — ${task.due_date}`}
                  style={{
                    position: 'absolute',
                    left: `calc(${pct}% - 8px)`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: color,
                    border: '2px solid white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    zIndex: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/projects/detail/${projectId}/task/detail/${task.id}`)}
                />
                <span style={{
                  position: 'absolute',
                  left: `calc(${pct}% + 12px)`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  color: 'var(--tblr-body-color)'
                }}>
                  {getStatusBadge(task)}
                  {' '}{task.title}
                </span>
              </div>
            );
          })}
        </div>

        {tasksWithoutDate.length > 0 && (
          <div className="mt-3 pt-2" style={{ borderTop: '1px solid var(--tblr-border-color)' }}>
            <div className="text-muted mb-2" style={{ fontSize: '12px' }}>{t('projects.tasks.noDate')}</div>
            {tasksWithoutDate.map(task => (
              <Button
                key={task.id}
                variant="outline-secondary"
                size="sm"
                className="me-1 mb-1"
                onClick={() => navigate(`/projects/detail/${projectId}/task/detail/${task.id}`)}
              >
                {task.title}
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <CenteredLoader />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const statusFilterOptions = [
    { value: null, label: t('projects.tasks.allStatuses') },
    ...taskStatuses.map(s => ({ value: s.id, label: s.name }))
  ];
  const priorityFilterOptions = [
    { value: null, label: t('projects.tasks.allPriorities') },
    ...taskPriorities.map(p => ({ value: p.id, label: p.name }))
  ];
  const selectedAssignedToOption = crewOptions.find(o =>
    o.value !== null && formData.assigned_to.includes(o.value)
  ) || null;

  return (
    <>
      <PageSection
        title={t('projects.tasks.title')}
        description={t('projects.tasks.description')}
        bodyClassName="p-0"
        actions={
          <div className="d-flex flex-wrap gap-2">
            <div className="btn-group btn-group-sm">
              <Button
                variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setViewMode('list')}
                title={t('projects.tasks.listView')}
              >
                <i className="ti ti-list me-1" aria-hidden="true"></i>
                {t('projects.tasks.listView')}
              </Button>
              <Button
                variant={viewMode === 'timeline' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setViewMode('timeline')}
                title={t('projects.tasks.timelineView')}
              >
                <i className="ti ti-timeline me-1" aria-hidden="true"></i>
                {t('projects.tasks.timelineView')}
              </Button>
            </div>
            {canManage && (
              <Button variant="primary" size="sm" onClick={handleOpenCreate}>
                <i className="ti ti-plus me-1" aria-hidden="true"></i>
                {t('projects.tasks.createTask')}
              </Button>
            )}
          </div>
        }
      >
          <div className="px-3 pt-3">
            {error ? <Alert variant="danger" className="app-inline-alert">{error}</Alert> : null}

            <div className="mb-3 row">
            <div className="col-md-4 mb-2 mb-md-0">
              <InputGroup>
                <Form.Control
                  placeholder={t('projects.tasks.searchPlaceholder')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="col-md-8 d-flex gap-2">
              <Select
                value={selectedStatus || statusFilterOptions[0]}
                onChange={setSelectedStatus}
                options={statusFilterOptions}
                isClearable={false}
                theme={selectTheme}
                className="react-select-container flex-grow-1"
                classNamePrefix="react-select"
              />
              <Select
                value={selectedPriority || priorityFilterOptions[0]}
                onChange={setSelectedPriority}
                options={priorityFilterOptions}
                isClearable={false}
                theme={selectTheme}
                className="react-select-container flex-grow-1"
                classNamePrefix="react-select"
              />
              <Select
                value={selectedAssignee}
                onChange={setSelectedAssignee}
                options={crewOptions}
                placeholder={t('projects.tasks.filterByAssignee')}
                isClearable
                theme={selectTheme}
                className="react-select-container flex-grow-1"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          </div>

          {tasks.length === 0 ? (
            <div className="px-3 pb-3">
              <EmptyState
                title={t('projects.tasks.noTasks')}
                message={t('projects.tasks.emptyMessage')}
                action={canManage ? (
                  <Button variant="primary" onClick={handleOpenCreate}>
                    <i className="ti ti-plus me-2" aria-hidden="true"></i>
                    {t('projects.tasks.createTask')}
                  </Button>
                ) : null}
              />
            </div>
          ) : viewMode === 'timeline' ? (
            <div className="px-3 pb-3">{renderTimeline()}</div>
          ) : (
            <Table responsive hover className="app-table mb-0">
              <thead>
                <tr>
                  <th>{t('projects.tasks.taskName')}</th>
                  <th>{t('projects.tasks.status')}</th>
                  <th>{t('projects.tasks.priority')}</th>
                  <th>{t('projects.tasks.dueDate')}</th>
                  <th>{t('projects.tasks.assignedTo')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => (
                  <tr key={task.id}>
                    <td>{task.title}</td>
                    <td>{getStatusBadge(task)}</td>
                    <td>{getPriorityBadge(task)}</td>
                    <td>{task.due_date ? formatDate(task.due_date) : t('common.notSet')}</td>
                    <td>{getAssignedToDisplay(task)}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/projects/detail/${projectId}/task/detail/${task.id}`)}
                          title={t('common.view')}
                        >
                          <i className="ti ti-eye me-1" aria-hidden="true"></i>
                          {t('common.view')}
                        </Button>
                        {canManage && (
                          <>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleEdit(task)}
                              title={t('common.edit')}
                            >
                              <i className="ti ti-edit me-1" aria-hidden="true"></i>
                              {t('common.edit')}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(task.id)}
                              title={t('common.delete')}
                            >
                              <i className="ti ti-trash me-1" aria-hidden="true"></i>
                              {t('common.delete')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
      </PageSection>

      {/* Create / Edit Task Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg" fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTaskId ? t('projects.tasks.editTask') : t('projects.tasks.createTask')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" style={{ position: 'relative' }}>
                  <Form.Label>{t('projects.tasks.taskName')}</Form.Label>
                  <Form.Control
                    ref={titleInputRef}
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    onFocus={handleTitleFocus}
                    onBlur={handleTitleBlur}
                    autoComplete="off"
                    required
                  />
                  {showTitleSugg && titleSuggestions.length > 0 && (
                    <div className="task-history-dropdown">
                      {titleSuggestions.map((s, i) => (
                        <div
                          key={i}
                          className="task-history-item"
                          onMouseDown={() => handlePickSuggestion(s)}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.description')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.tasks.status')}</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">—</option>
                    {taskStatuses.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.tasks.priority')}</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">—</option>
                    {taskPriorities.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.tasks.dueDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.tasks.assignedTo')}</Form.Label>
                  <Select
                    value={selectedAssignedToOption}
                    onChange={handleAssignedToChange}
                    options={crewOptions}
                    placeholder={t('projects.tasks.selectAssignee')}
                    isClearable
                    theme={selectTheme}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => { setShowModal(false); resetForm(); }}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit">
                {editingTaskId ? t('common.save') : t('common.create')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProjectTasks;
